import { ITelemetryProperties } from '@fluidframework/common-definitions';

export interface Event {
  namespace?: string;
  category: string;
  props: ITelemetryProperties;
}

export enum EventCategory {
  Generic = 'Generic',
  Error = 'Error',
  UserAction = 'UserAction',
  Activity = 'Activity'
}

export interface ErrorObject {
  message?: string;
  stack?: string;
}

export interface TelemetryEvent extends ITelemetryProperties {
  eventName: string;
}

/**
 * subCategory - A keyword that appends to the end of the generic table and creates a new table
 * like so, Office_Fluid_{Namespace}_Generic_subCategory
 */
export interface GenericTelemetryEvent extends TelemetryEvent {
  subCategory?: string;
}

export const enum ActivityResultType {
  Success = 'Success',
  Failure = 'Failure',
  Cancelled = 'Cancelled'
}

/**
 * The name of the activity can be specified using eventName which comes from the Telemetry Event interface
 * Activity name gets appended to the Kusto table and results in a new table every time.
 * Please be mindful when adding new activities.
 */
export interface ActivityData extends TelemetryEvent {
  // Duration of the activity in ms
  durationMs?: number;
  // Whether the activity was successful or not
  succeeded?: boolean;
  // Overall result of the activity
  resultType?: ActivityResultType;
}

/**
 * User Actions need to be GDPR exportable and should follow certain naming conventions.
 * Please refer to the "INCLUSION LIST GUIDANCE TO FOLLOW" section at https://microsoft.sharepoint.com/teams/opgngpx/SitePages/Exporting%20OTel%20Data.aspx for naming conventions.
 * For UserActionData the actionName comes from eventName under TelemetryEvent
 */
export interface UserActionData extends ActivityData {
  /**
   * Id for the user action
   */
  actionId?: number;

  /**
   * CommandSurface for the action, Ribbon, Floatie, QAT etc.
   */
  commandSurface?: string;

  /**
   * The name of the parent of the user action, TabHome, TabInsert, FloatieText, FloatieTable, Dialog, ContextMenu, Jewel,
   * Toolbar, Canvas, KeyboardShortcut, QAT, StatusBar, NotificationBar, TellMe, Unknown etc.
   */
  parentName?: string;

  /**
   * TriggerMethod for the action, Keyboard, Mouse, Touch, Unknown etc.
   */
  triggerMethod?: string;

  /**
   * Describes editing type of user actions. This can be 'edit', 'undo' or 'redo'
   */
  editMode?: string;

  /**
   * The offset in MS since the start of the users session.
   */
  timeOffsetMs?: number;
}
